import {isBefore, parseISO, subDays} from 'date-fns';

import {HOME_PATH, START_NEW_ORDER_PATH} from '@/paths';

export const PersonalizedHomePageVariant = {
  FALLBACK: 'fallback', // local only variant (flag config not loaded)
  CONTROL: 'control',
  TREATMENT: 'treatment',
} as const;

export type PersonalizedHomePageVariation =
  (typeof PersonalizedHomePageVariant)[keyof typeof PersonalizedHomePageVariant];

const VARIATIONS: string[] = Object.values(PersonalizedHomePageVariant);

/**
 * Eppo hands back a bare `string`, so narrow it at the boundary. An arm we don't know about (one
 * added in the Eppo UI ahead of the code) is treated as 'fallback' — it keeps the user on
 * start-new-order and out of the exposure numbers rather than guessing.
 */
export const toPersonalizedHomePageVariation = (
  variation: string,
): PersonalizedHomePageVariation =>
  VARIATIONS.includes(variation)
    ? (variation as PersonalizedHomePageVariation)
    : PersonalizedHomePageVariant.FALLBACK;

/**
 * ezx570::Discovery::PersonalizedHomePage — the returning-customer landing page for a variation.
 * Treatment lands on `/home`; control and the not-loaded 'fallback' arm stay on
 * `/start-new-order`.
 */
export const landingPagePathForVariation = (variation: PersonalizedHomePageVariation) =>
  variation === PersonalizedHomePageVariant.TREATMENT ? HOME_PATH : START_NEW_ORDER_PATH;

/**
 * ezx570::Discovery::PersonalizedHomePage — the browser's record of the arm a subject was exposed
 * to, so pages that render an arm without tracking exposure (the search page) can wait until the
 * landing page has. Keyed by subject so a shared browser doesn't carry one user's exposure over to
 * another.
 */
export const personalizedHomePageExposedKey = (subject: string) =>
  `personalizedHomePageExposed_${subject}`;

/**
 * Customers whose first marketplace order came before the new Home launched could have used the
 * previous homepage, so they get the Welcome Back banner. The launch date comes from the
 * ezx570::Discovery::PersonalizedHomePage::WelcomeCutoffDate flag (yyyy-MM-dd); an empty or
 * invalid value hides the banner.
 */
export const orderedBeforePersonalizedHomePageLaunch = (
  daysSinceFirstMarketplaceOrder: number | null | undefined,
  welcomeCutoffDate: string,
  today = new Date(),
) =>
  daysSinceFirstMarketplaceOrder != null &&
  isBefore(subDays(today, daysSinceFirstMarketplaceOrder), parseISO(welcomeCutoffDate));
